import { useEffect, useState } from "react";
import { toast } from "sonner";
import { deleteLead, fetchLeads } from "@/lib/api";
import { Loader2, Trash2, Lock, LogOut, RefreshCw } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const PASSCODE = "letmein";
const STORE_KEY = "hsl-admin-ok";

export default function Admin() {
  const [authed, setAuthed] = useState(() => sessionStorage.getItem(STORE_KEY) === "1");
  const [pass, setPass] = useState("");
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const d = await fetchLeads();
      setLeads(d);
    } catch (e) {
      toast.error("Couldn't load leads");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authed) load();
  }, [authed]);

  const onLogin = (e) => {
    e.preventDefault();
    if (pass === PASSCODE) {
      sessionStorage.setItem(STORE_KEY, "1");
      setAuthed(true);
    } else {
      toast.error("Wrong passcode");
    }
  };

  const onLogout = () => {
    sessionStorage.removeItem(STORE_KEY);
    setAuthed(false);
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    try {
      await deleteLead(id);
      setLeads((rows) => rows.filter((r) => r.id !== id));
      toast.success("Lead deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const filtered = leads.filter((l) => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return [l.name, l.email, l.brand, l.message, l.phone]
      .filter(Boolean)
      .some((s) => String(s).toLowerCase().includes(q));
  });

  if (!authed) {
    return (
      <div
        data-testid="admin-gate"
        className="min-h-[70vh] grid place-items-center px-5 py-20"
      >
        <form
          onSubmit={onLogin}
          className="w-full max-w-md border border-zinc-900 p-8"
        >
          <Lock size={20} className="text-[#FF1111]" />
          <h1 className="font-display uppercase text-4xl mt-4 leading-none">
            The <span className="text-[#FF1111]">lab</span> is locked.
          </h1>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 mt-3">
            Enter passcode
          </p>
          <input
            data-testid="admin-passcode"
            type="password"
            value={pass}
            onChange={(e) => setPass(e.target.value)}
            placeholder="passcode"
            className="mt-2 w-full bg-transparent border-b-2 border-zinc-900 focus:border-[#FF1111] outline-none py-2 font-mono text-lg"
            autoFocus
          />
          <button
            type="submit"
            data-testid="admin-login"
            className="mt-6 w-full bg-[#FF1111] text-white px-4 py-3 font-mono text-xs uppercase tracking-[0.2em] hover:bg-black transition-colors duration-200"
          >
            Enter
          </button>
          <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">
            Hint: this is a demo gate — passcode is <span className="text-zinc-700">letmein</span>
          </p>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="admin-page" className="px-5 md:px-10 py-10">
      <div className="flex flex-wrap items-end justify-between gap-4 border-b border-zinc-900 pb-6">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">
            [ Admin / Leads ]
          </p>
          <h1 className="font-display uppercase text-5xl md:text-7xl leading-[0.85] mt-2">
            Inbound <span className="text-[#FF1111]">briefs</span>
          </h1>
        </div>
        <div className="flex gap-2">
          <button
            onClick={load}
            data-testid="admin-refresh"
            className="inline-flex items-center gap-2 border border-zinc-900 px-4 py-2 font-mono text-xs uppercase tracking-[0.2em] hover:bg-zinc-900 hover:text-white transition-colors duration-200"
          >
            <RefreshCw size={14} /> Refresh
          </button>
          <button
            onClick={onLogout}
            data-testid="admin-logout"
            className="inline-flex items-center gap-2 border border-zinc-900 px-4 py-2 font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#FF1111] hover:text-white hover:border-[#FF1111] transition-colors duration-200"
          >
            <LogOut size={14} /> Logout
          </button>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-4">
        <input
          data-testid="admin-search"
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search name, email, brand..."
          className="flex-1 min-w-[240px] bg-transparent border-b-2 border-zinc-900 focus:border-[#FF1111] outline-none py-2 font-mono text-sm"
        />
        <span
          data-testid="admin-count"
          className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500"
        >
          {filtered.length} / {leads.length} leads
        </span>
      </div>

      <div className="mt-6 border border-zinc-900 overflow-x-auto">
        {loading ? (
          <div className="p-10 flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">
            <Loader2 size={16} className="animate-spin" /> Loading briefs...
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-10 font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">
            No leads yet. Send a brief from /contact to see one here.
          </div>
        ) : (
          <Table data-testid="admin-table">
            <TableHeader>
              <TableRow className="bg-zinc-50">
                <TableHead className="font-mono uppercase tracking-[0.15em] text-[10px]">When</TableHead>
                <TableHead className="font-mono uppercase tracking-[0.15em] text-[10px]">Name</TableHead>
                <TableHead className="font-mono uppercase tracking-[0.15em] text-[10px]">Email</TableHead>
                <TableHead className="font-mono uppercase tracking-[0.15em] text-[10px]">Brand</TableHead>
                <TableHead className="font-mono uppercase tracking-[0.15em] text-[10px]">Budget</TableHead>
                <TableHead className="font-mono uppercase tracking-[0.15em] text-[10px]">Message</TableHead>
                <TableHead className="w-16" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((l) => (
                <TableRow key={l.id} data-testid={`admin-row-${l.id}`} className="align-top">
                  <TableCell className="font-mono text-xs whitespace-nowrap">
                    {formatDate(l.created_at)}
                  </TableCell>
                  <TableCell className="font-grotesk font-bold">{l.name}</TableCell>
                  <TableCell className="font-mono text-xs">
                    <a href={`mailto:${l.email}`} className="link-slash">{l.email}</a>
                    {l.phone && <div className="text-zinc-500 mt-1">{l.phone}</div>}
                  </TableCell>
                  <TableCell className="font-grotesk">{l.brand || "—"}</TableCell>
                  <TableCell className="font-mono text-xs">{l.budget || "—"}</TableCell>
                  <TableCell className="font-grotesk text-sm max-w-md">
                    <p className="line-clamp-3">{l.message}</p>
                  </TableCell>
                  <TableCell>
                    <button
                      onClick={() => onDelete(l.id)}
                      data-testid={`admin-delete-${l.id}`}
                      className="p-2 hover:bg-[#FF1111] hover:text-white transition-colors duration-200"
                      aria-label="Delete lead"
                    >
                      <Trash2 size={14} />
                    </button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
